import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Box, Grid, Typography, Paper, Button, Skeleton, Alert, Stack,
  Chip, Divider, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Avatar, Snackbar
} from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import ShareRoundedIcon from '@mui/icons-material/ShareRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import API_BASE_URL from '../../Config/config';
import { useNextGenAuth } from '../context/NextGenAuthContext';
import {
  TOKENS, PageContainer, PageHeader, SectionCard, SectionTitle,
  StatCard, EmptyState, GradientButton, fadeUp,
} from '../components/NextGenPortalUI';

const STATUS_COLORS = {
  successful: { bg: 'rgba(22,163,74,0.1)', color: '#16A34A', border: 'rgba(22,163,74,0.25)', label: 'Successful' },
  pending: { bg: 'rgba(245,158,11,0.12)', color: '#B45309', border: 'rgba(245,158,11,0.26)', label: 'Pending' },
  rejected: { bg: 'rgba(239,68,68,0.1)', color: '#DC2626', border: 'rgba(239,68,68,0.24)', label: 'Rejected' },
};

const CopyButton = ({ text, label = 'Copy' }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <Button
      onClick={handleCopy}
      startIcon={copied ? <CheckRoundedIcon sx={{ fontSize: '16px !important' }} /> : <ContentCopyRoundedIcon sx={{ fontSize: '16px !important' }} />}
      sx={{
        flexShrink: 0,
        color: copied ? '#16A34A' : TOKENS.brand.primary,
        bgcolor: copied ? 'rgba(22,163,74,0.1)' : '#FFFFFF',
        border: `1.5px solid ${copied ? 'rgba(22,163,74,0.3)' : 'rgba(108,59,255,0.3)'}`,
        borderRadius: TOKENS.radius.control,
        fontWeight: 700,
        fontSize: '0.8rem',
        textTransform: 'none',
        px: 1.85,
        py: 0.85,
        whiteSpace: 'nowrap',
        transition: 'all 0.2s ease',
        boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
        '&:hover': {
          bgcolor: copied ? 'rgba(22,163,74,0.16)' : 'rgba(108,59,255,0.1)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      {copied ? 'Copied!' : label}
    </Button>
  );
};

const REWARD_PLANS = [
  { plan: 'Starter', price: '₹9,999', you: '₹500', friend: '₹500', color: '#06B6D4', icon: <RocketLaunchRoundedIcon /> },
  { plan: 'Professional', price: '₹19,999', you: '₹1,000', friend: '₹1,000', color: '#6C3BFF', icon: <EmojiEventsRoundedIcon /> },
  { plan: 'Enterprise', price: '₹49,999', you: '₹2,500', friend: '₹2,500', color: '#F59E0B', icon: <WorkspacePremiumRoundedIcon /> },
];

const NextGenReferral = () => {
  const { nextgen_builder } = useNextGenAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const fetchReferral = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/referral`);
        setData(res.data.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load referral data');
      } finally {
        setLoading(false);
      }
    };
    fetchReferral();
  }, [nextgen_builder?.id]);

  const handleShare = () => {
    const link = data?.referralLink;
    if (!link) return;
    if (navigator.share) {
      navigator.share({
        title: 'Join The Skill Genie NextGen Builder',
        text: `Use my referral code ${data.referralCode} to get a discount on NextGen Builder!`,
        url: link,
      });
    } else {
      navigator.clipboard.writeText(link);
      setSnack({ open: true, message: 'Link copied to clipboard!', severity: 'success' });
    }
  };

  const stats = data?.stats || {};

  const statCards = [
    { icon: <AccountBalanceWalletRoundedIcon />, label: 'Total Earnings', value: loading ? '' : `₹${(stats.totalEarnings || 0).toLocaleString('en-IN')}`, color: '#16A34A' },
    { icon: <PeopleRoundedIcon />, label: 'Total Referrals', value: loading ? '' : (stats.total || 0), color: '#6C3BFF' },
    { icon: <CheckRoundedIcon />, label: 'Successful', value: loading ? '' : (stats.successful || 0), color: '#16A34A' },
    { icon: <HourglassEmptyRoundedIcon />, label: 'Pending', value: loading ? '' : (stats.pending || 0), color: '#F59E0B' },
  ];

  return (
    <motion.div {...fadeUp}>
      <PageContainer>

        <PageHeader
          icon={<CardGiftcardRoundedIcon />}
          eyebrow="REFERRALS"
          title="Refer & Earn"
          description="Share your code. Your friends save money. You earn wallet credits."
        />

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {/* ── Referral Code (centerpiece) + Link ── */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>

          {/* Referral Code */}
          <Grid item xs={12} md={7}>
            <SectionCard
              sx={{
                height: '100%',
                background: 'linear-gradient(135deg, rgba(108,59,255,0.05) 0%, rgba(139,92,246,0.03) 100%)',
                border: '1px solid rgba(108,59,255,0.22)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <CardGiftcardRoundedIcon sx={{ color: TOKENS.brand.primaryLight, fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TOKENS.text.primary }}>
                  Your Referral Code
                </Typography>
              </Box>

              {loading ? (
                <Skeleton variant="rectangular" height={92} sx={{ borderRadius: '14px' }} />
              ) : (
                <Box
                  sx={{
                    display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between',
                    gap: 1.5,
                    p: { xs: 2.25, sm: 2.75 }, borderRadius: '14px',
                    background: 'rgba(255,255,255,0.7)',
                    border: '2px dashed rgba(108,59,255,0.35)',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: '"Roboto Mono", "Plus Jakarta Sans", monospace',
                      fontWeight: 900,
                      color: TOKENS.brand.primary,
                      letterSpacing: '0.2em',
                      fontSize: { xs: '1.7rem', sm: '2.1rem', md: '2.35rem' },
                      lineHeight: 1.1,
                    }}
                  >
                    {data?.referralCode || '—'}
                  </Typography>
                  <CopyButton text={data?.referralCode || ''} label="Copy Code" />
                </Box>
              )}
              <Typography variant="caption" sx={{ color: TOKENS.text.muted, display: 'block', mt: 1.75, lineHeight: 1.6, fontWeight: 500 }}>
                Share this code with friends at checkout. They save on their first plan,
                and you earn wallet credits automatically!
              </Typography>
            </SectionCard>
          </Grid>

          {/* Referral Link */}
          <Grid item xs={12} md={5}>
            <SectionCard sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <LinkRoundedIcon sx={{ color: TOKENS.brand.primaryLight, fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TOKENS.text.primary }}>
                  Your Referral Link
                </Typography>
              </Box>

              {loading ? (
                <Skeleton variant="rectangular" height={44} sx={{ borderRadius: '10px', mb: 1.5 }} />
              ) : (
                <Box
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    p: 1.5, borderRadius: '10px', mb: 1.5,
                    background: TOKENS.bg.page,
                    border: `1px solid ${TOKENS.border}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      flex: 1, color: TOKENS.text.secondary,
                      wordBreak: 'break-all',
                      fontFamily: '"Roboto Mono", "Plus Jakarta Sans", monospace',
                      fontSize: '0.75rem',
                    }}
                  >
                    {data?.referralLink || '—'}
                  </Typography>
                  <CopyButton text={data?.referralLink || ''} label="Copy Link" />
                </Box>
              )}

              <Box sx={{ mt: 'auto' }}>
                <GradientButton
                  fullWidth
                  onClick={handleShare}
                  disabled={loading}
                  endIcon={null}
                  sx={{ justifyContent: 'center' }}
                >
                  <ShareRoundedIcon sx={{ fontSize: 18, mr: 1 }} />
                  Share Referral Link
                </GradientButton>
              </Box>
            </SectionCard>
          </Grid>
        </Grid>

        {/* ── Stats Grid ── */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {statCards.map((stat, i) => (
            <Grid item xs={6} sm={3} key={i}>
              <StatCard {...stat} loading={loading} />
            </Grid>
          ))}
        </Grid>

        {/* ── Reward Structure ── */}
        <SectionCard sx={{ mb: 3 }}>
          <SectionTitle icon={<EmojiEventsRoundedIcon />} title="Reward Structure" />
          <Grid container spacing={1.75}>
            {REWARD_PLANS.map((item) => (
              <Grid item xs={12} sm={4} key={item.plan}>
                <Box
                  component={motion.div}
                  whileHover={{ y: -3, boxShadow: `0 12px 28px ${item.color}22` }}
                  transition={{ duration: 0.2 }}
                  sx={{
                    p: 2.25, borderRadius: '16px',
                    background: `${item.color}08`,
                    border: `1px solid ${item.color}26`,
                    boxShadow: TOKENS.shadow.card,
                    textAlign: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: 40, height: 40, borderRadius: '12px', mx: 'auto', mb: 1.25,
                      background: `${item.color}16`, border: `1px solid ${item.color}30`,
                      color: item.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      '& svg': { fontSize: 20 },
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: item.color, mb: 0.25, fontSize: '0.92rem' }}>
                    {item.plan}
                  </Typography>
                  <Typography variant="caption" sx={{ color: TOKENS.text.muted, display: 'block', mb: 1.25, fontWeight: 500 }}>
                    {item.price} plan
                  </Typography>
                  <Divider sx={{ borderColor: `${item.color}20`, mb: 1.25 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: TOKENS.text.muted, display: 'block', fontSize: '0.67rem', fontWeight: 600 }}>
                        You earn
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: '#16A34A', fontSize: '0.92rem' }}>
                        {item.you}
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem sx={{ borderColor: `${item.color}20` }} />
                    <Box>
                      <Typography variant="caption" sx={{ color: TOKENS.text.muted, display: 'block', fontSize: '0.67rem', fontWeight: 600 }}>
                        Friend saves
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: item.color, fontSize: '0.92rem' }}>
                        {item.friend}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </SectionCard>

        {/* ── Recent Activity ── */}
        <SectionCard padding={0} sx={{ overflow: 'hidden' }}>
          <Box sx={{ px: { xs: 2.25, md: 3 }, pt: { xs: 2.25, md: 2.75 }, pb: 1.75 }}>
            <SectionTitle icon={<TrendingUpRoundedIcon />} title="Recent Referral Activity" sx={{ mb: 0 }} />
          </Box>
          <Divider sx={{ borderColor: TOKENS.border }} />

          {loading ? (
            <Box sx={{ p: 2.5 }}>
              <Stack spacing={1.25}>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={52} sx={{ borderRadius: '10px' }} />
                ))}
              </Stack>
            </Box>
          ) : (data?.recentActivity?.length || 0) === 0 ? (
            <EmptyState
              icon={<CardGiftcardRoundedIcon />}
              title="No Referral Activity Yet"
              description="Share your code to start earning wallet credits."
              action={
                <GradientButton onClick={handleShare} endIcon={null}>
                  <ShareRoundedIcon sx={{ fontSize: 17, mr: 1 }} />
                  Share Referral Link
                </GradientButton>
              }
            />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Referred Person', 'Plan', 'Status', 'You Earned', 'Date'].map((h) => (
                      <TableCell key={h} sx={{ color: TOKENS.text.secondary, fontWeight: 700, fontSize: '0.75rem' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.recentActivity.map((row) => {
                    const s = STATUS_COLORS[row.referral_status] || STATUS_COLORS.pending;
                    return (
                      <TableRow key={row.id} sx={{ '&:hover': { bgcolor: 'rgba(108,59,255,0.04)' } }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            <Avatar
                              sx={{
                                width: 30, height: 30,
                                bgcolor: 'rgba(108,59,255,0.1)',
                                color: TOKENS.brand.primary,
                                fontSize: '0.75rem',
                                fontWeight: 800,
                                border: '1px solid rgba(108,59,255,0.2)',
                              }}
                            >
                              {row.referred_name?.charAt(0)?.toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ color: TOKENS.text.primary, fontWeight: 600, fontSize: '0.85rem' }}>
                                {row.referred_name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: TOKENS.text.muted }}>
                                {row.referred_email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: TOKENS.text.secondary, textTransform: 'capitalize', fontWeight: 500 }}>
                            {row.subscription_plan || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={s.label}
                            size="small"
                            sx={{
                              bgcolor: s.bg,
                              color: s.color,
                              border: `1px solid ${s.border}`,
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              height: 24,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 800,
                              color: row.referral_status === 'successful' ? '#16A34A' : TOKENS.text.muted,
                              fontSize: '0.88rem',
                            }}
                          >
                            {row.referral_status === 'successful'
                              ? `₹${parseFloat(row.wallet_credit || 0).toLocaleString('en-IN')}`
                              : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: TOKENS.text.muted, fontWeight: 500 }}>
                            {new Date(row.created_at).toLocaleDateString('en-IN', {
                              day: '2-digit', month: 'short', year: 'numeric',
                            })}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </SectionCard>

        <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          <Alert severity={snack.severity}>{snack.message}</Alert>
        </Snackbar>
      </PageContainer>
    </motion.div>
  );
};

export default NextGenReferral;
